 import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Send, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  ExternalLink 
} from "lucide-react";

export default function ContactUs() {
  const location = useLocation();
  const propertyInfo = location.state?.propertyInfo;

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    interest: "General Inquiry",
    message: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (propertyInfo) {
      setFormData((prev) => ({
        ...prev,
        interest: propertyInfo.interest || "Buy Property",
        message: `Hello, I am interested in inquiring about the following property:

Property Title: ${propertyInfo.title || "N/A"}
Price: ${propertyInfo.price || "N/A"}
Location: ${propertyInfo.location || "St. John, USVI"}
MLS / Ref #: ${propertyInfo.mlsNumber || "N/A"}

Please contact me with further details or to schedule a private showing. Thank you!`
      }));
    }
  }, [propertyInfo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const payload = {
      name: formData.name,
      email: formData.email,
      mobile: formData.mobile,
      interest: formData.interest,
      message: `Interest: ${formData.interest}\n\n${formData.message}`
    };

    try {
      const response = await fetch("https://340realestate.com/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "*/*"
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuccess(true);
        setFormData({
          name: "",
          mobile: "",
          email: "",
          interest: "General Inquiry",
          message: ""
        });
      } else {
        const errorText = await response.text();
        throw new Error(errorText || "Server responded with an error.");
      }
    } catch (err) {
      console.error("Error submitting lead form:", err);
      setError(
        "There was a problem sending your message. Please check your connection or try again later."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-brand-light flex flex-col font-sans">
      <Navbar isTransparent={false} />

      <section className="flex-grow bg-[#FAF6F0] py-16 lg:py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          
          {/* Left Column: Contact details & Map */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 flex flex-col gap-10"
          >
            <div>
              <p className="text-tropical-500 text-[11px] font-bold uppercase tracking-[0.25em] mb-4">
                Get In Touch
              </p>
              <h1 className="text-[2.5rem] sm:text-[3.2rem] font-bold text-ink-900 leading-tight mb-6 font-display">
                We'd love to hear from you.
              </h1>
              <p className="text-ink-600 text-[15px] leading-relaxed font-light">
                Reach out directly using the form, or drop us an email, or phone call. Our team generally responds within 24 hours.
              </p>
            </div>

            {/* Address, Phone, Email list */}
            <div className="flex flex-col gap-6">
              {/* Office Location */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white border border-[#E8E3DC] flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-brand-dark" />
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-ink-900 mb-1">
                    Office Location
                  </h4>
                  <a 
                    href="https://maps.google.com/?q=340+Real+Estate+Company,+St.+John,+VI"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ink-600 hover:text-tropical-500 transition-colors text-sm font-light leading-relaxed block"
                  >
                    340 Real Estate Company<br />
                    PO Box 766<br />
                    St John, VI 00831
                  </a>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white border border-[#E8E3DC] flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-brand-dark" />
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-ink-900 mb-1">
                    Phone
                  </h4>
                  <p className="text-ink-600 text-sm font-light leading-relaxed">
                    <a href="tel:+13406436068" className="hover:text-tropical-500 transition-colors">
                      +1 (340) 643-6068
                    </a>
                    <br />
                    <a href="tel:+13407794478" className="hover:text-tropical-500 transition-colors">
                      +1 (340) 779-4478
                    </a>
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white border border-[#E8E3DC] flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-brand-dark" />
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-ink-900 mb-1">
                    Email
                  </h4>
                  <p className="text-ink-600 text-sm font-light leading-relaxed">
                    <a href="mailto:340realestateco@gmail.com" className="hover:text-tropical-500 transition-colors">
                      340realestateco@gmail.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Embedded Google Map */}
            <div className="relative rounded-[2rem] overflow-hidden shadow-md border border-[#E8E3DC] h-[280px] sm:h-[350px]">
              <iframe
                title="Office Location Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14902.946399066667!2d-64.7962804!3d18.33128!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8c0515ad47a82ed3%3A0xe5064e4ebca2d2f4!2s340%20Real%20Estate%20Company!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
              <a
                href="https://maps.google.com/?q=340+Real+Estate+Company,+St.+John,+VI"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2.5 rounded-xl text-xs font-bold text-ink-900 border border-[#E8E3DC] shadow-md flex items-center gap-1.5 hover:bg-white hover:text-tropical-600 transition-all hover:scale-105"
              >
                Open in Maps <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </motion.div>

          {/* Right Column: Contact form Card */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-7 w-full"
          >
            <div className="bg-white rounded-[2.5rem] shadow-xl p-8 sm:p-12 border border-[#E8E3DC]">
              {success ? (
                /* Success Message Screen */
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center text-center py-8"
                >
                  <div className="w-16 h-16 bg-tropical-50 border border-tropical-200 rounded-full flex items-center justify-center mb-6 text-tropical-500 shadow-sm">
                    <CheckCircle2 className="w-10 h-10" />
                  </div>
                  <h2 className="text-3xl font-bold text-ink-900 font-display mb-4">
                    Message Sent!
                  </h2>
                  <p className="text-ink-600 text-sm leading-relaxed max-w-md mb-8 font-light">
                    Thank you for reaching out. We have received your inquiry regarding **{formData.interest}** and will respond within 24 hours.
                  </p>
                  <button
                    onClick={() => setSuccess(false)}
                    className="px-8 py-3.5 bg-brand-dark hover:bg-brand-dark/95 active:scale-[0.98] text-white text-[13px] font-bold uppercase tracking-wider rounded-xl transition-all shadow-md font-sans"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              ) : (
                /* Contact Form */
                <>
                  <div className="mb-8">
                    <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 font-display mb-2">
                      Send a Message
                    </h2>
                    <p className="text-ink-600 text-sm">
                      Fill out the form below and we will get back to you shortly.
                    </p>
                  </div>

                  {error && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-start gap-3 text-sm">
                      <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
                      <div>
                        <span className="font-semibold">Submission Error:</span> {error}
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                    {/* Row 1: Name and Mobile */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col">
                        <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-ink-900 mb-2">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-tropical-500/20 focus:border-tropical-500 transition-all text-sm font-light"
                        />
                      </div>

                      <div className="flex flex-col">
                        <label htmlFor="mobile" className="text-xs font-bold uppercase tracking-wider text-ink-900 mb-2">
                          Mobile Number
                        </label>
                        <input
                          type="text"
                          id="mobile"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleChange}
                          placeholder="+1 (340) 000-0000"
                          className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-tropical-500/20 focus:border-tropical-500 transition-all text-sm font-light"
                        />
                      </div>
                    </div>

                    {/* Row 2: Email */}
                    <div className="flex flex-col">
                      <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-ink-900 mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@gmail.com"
                        className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-tropical-500/20 focus:border-tropical-500 transition-all text-sm font-light"
                      />
                    </div>

                    {/* Row 3: Interest Dropdown */}
                    <div className="flex flex-col">
                      <label htmlFor="interest" className="text-xs font-bold uppercase tracking-wider text-ink-900 mb-2">
                        I am interested in...
                      </label>
                      <div className="relative">
                        <select
                          id="interest"
                          name="interest"
                          value={formData.interest}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-ink-900 focus:outline-none focus:ring-2 focus:ring-tropical-500/20 focus:border-tropical-500 transition-all text-sm font-light appearance-none cursor-pointer"
                        >
                          <option value="General Inquiry">General Inquiry</option>
                          <option value="Buy Property">Buy Property</option>
                          <option value="Buy Land">Buy Land</option>
                          <option value="Villa Rental">Villa Rental</option>
                          <option value="Sell Property">Sell Property</option>
                          <option value="Property Management">Property Management</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-ink-600">
                          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Row 4: Message */}
                    <div className="flex flex-col">
                      <label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-ink-900 mb-2">
                        Your Message <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows="5"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="How can we help you?"
                        className="w-full px-4 py-3 bg-[#FAF8F5] border border-[#E8E3DC] rounded-xl text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-tropical-500/20 focus:border-tropical-500 transition-all text-sm font-light resize-none"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full py-4 bg-brand-dark hover:bg-brand-dark/95 active:scale-[0.99] text-white text-[13px] font-bold uppercase tracking-widest rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-brand-dark/20 cursor-pointer ${
                        isSubmitting ? "opacity-85 cursor-not-allowed" : ""
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>

        </div>
      </section>

      <Footer />
    </main>
  );
}
